import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  getTreeFilteredFields,
  TypesUtilsService,
} from '@osos/shared/legacy-core';
import {
  ContextualHelpModel,
  FieldPresenterModel,
  helpContextEnum,
  LocalizedValueModel,
} from '@osos/shared/types';
import { HelpContent } from './types';

@Injectable({
  providedIn: 'root',
})
export class HelpAndDocumentationService {
  protected readonly typesUtilsService = inject(TypesUtilsService);
  protected readonly translateService = inject(TranslateService);

  #content = signal<HelpContent[] | null>(null);
  readonly content = this.#content.asReadonly();

  setContentArray(content: HelpContent[] | null): void {
    this.#content.set(content);
  }

  clearContent(): void {
    this.#content.set(null);
  }

  setContent(content: string | LocalizedValueModel | null): void;
  setContent(
    viewName: string,
    content: string | LocalizedValueModel | null,
  ): void;
  setContent(
    viewNameOrContent: string | LocalizedValueModel | null,
    content?: string | LocalizedValueModel | null,
  ): void {
    if (arguments.length === 1) {
      const singleContent = viewNameOrContent;
      if (!singleContent) {
        this.#content.set(null);
        return;
      }

      const contentString =
        typeof singleContent === 'string'
          ? singleContent
          : this.typesUtilsService.getLocaleString(singleContent);

      this.#content.set([
        {
          title: '',
          content: contentString,
        },
      ]);
      return;
    }

    const viewName = viewNameOrContent as string;
    if (!content) {
      this.#content.set(null);
      return;
    }

    const contentString =
      typeof content === 'string'
        ? content
        : this.typesUtilsService.getLocaleString(content);

    const helpContent: HelpContent = {
      title: viewName,
      content: contentString,
    };

    this.#content.set([helpContent]);
  }

  setViewContextualHelp(
    viewName: LocalizedValueModel | null,
    help: ContextualHelpModel,
    context: helpContextEnum,
    entityFields?: FieldPresenterModel[] | null,
  ): void {
    const helpContentArray = this._getViewContextualHelp(
      viewName,
      help,
      context,
      entityFields,
    );
    this.#content.set(helpContentArray.length > 0 ? helpContentArray : null);
  }

  seAdminContextualHelp(
    context: helpContextEnum,
    entityFields?: FieldPresenterModel[] | null,
  ): void {
    const adminHelp = this._getAdminContextualHelp(context, entityFields);
    if (
      adminHelp?.every((h) => !h.children?.length) &&
      adminHelp?.every((h) => !h.content)
    ) {
      this.clearContent();
      return;
    }
    this.#content.set(adminHelp);
  }
  private _getAdminContextualHelp(
    context: helpContextEnum,
    entityFields?: FieldPresenterModel[] | null,
  ) {
    const flattedFields = getTreeFilteredFields(
      entityFields,
      () => true,
    ).reduce(
      (acc, val) => ({
        ...acc,
        [val._normalizedName]: {
          name: val.name,
          contextualHelp: val.options?.help?.contextualHelp,
          children: val.block?.internalEntity?.fields.reduce(
            (childAcc, childVal) => ({
              ...childAcc,
              [childVal._normalizedName]: {
                name: childVal.name,
                contextualHelp: childVal.options?.help?.contextualHelp,
              },
            }),
            {},
          ),
        },
      }),
      {},
    );
    const contextObj: HelpContent = {
      title: '',
      content: '',
      children: [],
    };

    const appendFieldsHelp = () => {
      if (!flattedFields) return;
      Object.keys(flattedFields).forEach((field) => {
        if (flattedFields[field].contextualHelp) {
          contextObj.children.push(
            this._getFieldContextualHelp(flattedFields, {
              fieldName: field,
              contextualHelp: flattedFields[field].contextualHelp,
            }),
          );
        }
      });
    };
    switch (context) {
      case helpContextEnum.EntriesList:
        contextObj.title = this.translateService.instant(
          'labels.ENTRIES_LIST_HELP',
        );
        appendFieldsHelp();
        break;
      case helpContextEnum.EntryDetails:
        contextObj.title = this.translateService.instant(
          'labels.ENTRY_DETAILS_HELP',
        );
        appendFieldsHelp();

        break;
      case helpContextEnum.CreateEntry:
        contextObj.title = this.translateService.instant(
          'labels.CREATE_ENTRY_HELP',
        );
        appendFieldsHelp();
        break;
      case helpContextEnum.EditEntry:
        contextObj.title = this.translateService.instant(
          'labels.EDIT_ENTRY_HELP',
        );
        appendFieldsHelp();
        break;
      case helpContextEnum.Dashboard:
        contextObj.title = this.translateService.instant(
          'labels.DASHBOARD_HELP',
        );

        break;
      case helpContextEnum.AskUser:
        contextObj.title = this.translateService.instant(
          'labels.ASK_USER_HELP',
        );
        appendFieldsHelp();
        break;
    }
    return [contextObj];
  }

  appendBlockRelationHelp(
    relationName: LocalizedValueModel | null,
    help: ContextualHelpModel,
    context: helpContextEnum,
    entityFields?: FieldPresenterModel[] | null,
  ) {
    const helpContentArray = this._getViewContextualHelp(
      relationName,
      help,
      context,
      entityFields,
    );
    if (helpContentArray.length > 0) {
      const currentContent = this.#content();
      if (currentContent) {
        this.#content.set([...currentContent, ...helpContentArray]);
      } else {
        this.#content.set(helpContentArray);
      }
    }
  }

  appendAdminBlockRelationHelp(
    context: helpContextEnum,
    entityFields?: FieldPresenterModel[] | null,
  ) {
    const helpContentArray = this._getAdminContextualHelp(
      context,
      entityFields,
    );
    if (helpContentArray.length > 0) {
      const currentContent = this.#content();
      if (currentContent) {
        this.#content.set([...currentContent, ...helpContentArray]);
      } else {
        this.#content.set(helpContentArray);
      }
    }
  }

  private _getViewContextualHelp(
    viewName: LocalizedValueModel | null,
    help: ContextualHelpModel,
    context: helpContextEnum,
    entityFields?: FieldPresenterModel[] | null,
  ) {
    const flattedFields = getTreeFilteredFields(
      entityFields,
      () => true,
    ).reduce(
      (acc, val) => ({
        ...acc,
        [val._normalizedName]: {
          name: val.name,
          contextualHelp: val.options?.help?.contextualHelp,
          children: val.block?.internalEntity?.fields.reduce(
            (childAcc, childVal) => ({
              ...childAcc,
              [childVal._normalizedName]: {
                name: childVal.name,
                contextualHelp: childVal.options?.help?.contextualHelp,
              },
            }),
            {},
          ),
        },
      }),
      {},
    );
    const helpContentArray: HelpContent[] = [];

    if (help?.contextualHelp || help?.fieldsContextualHelp?.length) {
      // const translatedViewName =
      //   this.typesUtilsService.getLocaleString(viewName);
      const generalContent = this.typesUtilsService.getLocaleString(
        help.contextualHelp,
      );
      const general: HelpContent = {
        title: this.typesUtilsService.getLocaleString(viewName),
        content: generalContent,
        children: [],
      };
      if (help?.fieldsContextualHelp?.length && flattedFields) {
        help.fieldsContextualHelp
          .filter(
            (fieldHelp) =>
              !!fieldHelp.contextualHelp && flattedFields[fieldHelp.fieldName],
          )
          .map((fieldHelp) => {
            general.children.push(
              this._getFieldContextualHelp(
                flattedFields,
                fieldHelp,
                help.fieldsContextualHelp,
              ),
            );
          });
        // check if some entity fields are not added to entity fields help
        if (flattedFields && Object.keys(flattedFields).length > 0) {
          Object.keys(flattedFields).forEach((field) => {
            if (
              (flattedFields[field]?.contextualHelp ||
                (flattedFields[field]?.children &&
                  Object.keys(flattedFields[field].children)?.some(
                    (x) => flattedFields[field].children[x].contextualHelp,
                  ))) &&
              !general.children.find(
                (eh) =>
                  eh.title ===
                  this.typesUtilsService.getLocaleString(
                    flattedFields[field].name,
                  ),
              )
            ) {
              general.children.push({
                title: this.typesUtilsService.getLocaleString(
                  flattedFields[field].name,
                ),
                content: this.typesUtilsService.getLocaleString(
                  flattedFields[field].contextualHelp,
                ),
                children: flattedFields[field].children
                  ? Object.keys(flattedFields[field].children)
                      ?.filter(
                        (x) => flattedFields[field].children[x].contextualHelp,
                      )
                      ?.map((childField) => ({
                        title: this.typesUtilsService.getLocaleString(
                          flattedFields[field].children[childField].name,
                        ),
                        content: this.typesUtilsService.getLocaleString(
                          flattedFields[field].children[childField]
                            .contextualHelp,
                        ),
                      })) || []
                  : [],
              });
            }
          });
        }
      }
      helpContentArray.push(general);
    }

    const contextObj: HelpContent = { title: '', content: '', children: [] };

    switch (context) {
      case helpContextEnum.EntriesList:
        contextObj.title = this.translateService.instant(
          'labels.ENTRIES_LIST_HELP',
        );
        contextObj.content = this.typesUtilsService.getLocaleString(
          help?.contextualHelpDetails?.entriesListHelp,
        );
        if (
          help?.contextualHelpDetails?.entriesListFieldsHelp?.length &&
          flattedFields
        ) {
          help?.contextualHelpDetails.entriesListFieldsHelp
            .filter((fieldHelp) => !!fieldHelp.contextualHelp)
            .map((fieldHelp) =>
              contextObj.children.push(
                this._getFieldContextualHelp(
                  flattedFields,
                  fieldHelp,
                  help.contextualHelpDetails?.entriesListFieldsHelp,
                ),
              ),
            );
        }
        break;
      case helpContextEnum.EntryDetails:
        contextObj.title = this.translateService.instant(
          'labels.ENTRY_DETAILS_HELP',
        );
        contextObj.content = this.typesUtilsService.getLocaleString(
          help?.contextualHelpDetails?.entryDetailsHelp,
        );
        if (
          help?.contextualHelpDetails?.entryDetailsFieldsHelp?.length &&
          flattedFields
        ) {
          help?.contextualHelpDetails.entryDetailsFieldsHelp
            .filter((fieldHelp) => !!fieldHelp.contextualHelp)
            .map((fieldHelp) =>
              contextObj.children.push(
                this._getFieldContextualHelp(
                  flattedFields,
                  fieldHelp,
                  help.contextualHelpDetails?.entryDetailsFieldsHelp,
                ),
              ),
            );
        }
        break;
      case helpContextEnum.CreateEntry:
        contextObj.title = this.translateService.instant(
          'labels.CREATE_ENTRY_HELP',
        );
        contextObj.content = this.typesUtilsService.getLocaleString(
          help?.contextualHelpDetails?.createEntryHelp,
        );
        if (
          help?.contextualHelpDetails?.createEntryFieldsHelp?.length &&
          flattedFields
        ) {
          help?.contextualHelpDetails.createEntryFieldsHelp
            .filter((fieldHelp) => !!fieldHelp.contextualHelp)
            .map((fieldHelp) =>
              contextObj.children.push(
                this._getFieldContextualHelp(
                  flattedFields,
                  fieldHelp,
                  help.contextualHelpDetails?.createEntryFieldsHelp,
                ),
              ),
            );
        }
        break;
      case helpContextEnum.EditEntry:
        contextObj.title = this.translateService.instant(
          'labels.EDIT_ENTRY_HELP',
        );
        contextObj.content = this.typesUtilsService.getLocaleString(
          help?.contextualHelpDetails?.editEntryHelp,
        );
        if (
          help?.contextualHelpDetails?.editEntryFieldsHelp?.length &&
          flattedFields
        ) {
          help?.contextualHelpDetails.editEntryFieldsHelp
            .filter((fieldHelp) => !!fieldHelp.contextualHelp)
            .map((fieldHelp) =>
              contextObj.children.push(
                this._getFieldContextualHelp(
                  flattedFields,
                  fieldHelp,
                  help.contextualHelpDetails?.editEntryFieldsHelp,
                ),
              ),
            );
        }
        break;
      case helpContextEnum.Dashboard:
        contextObj.title = this.translateService.instant(
          'labels.DASHBOARD_HELP',
        );
        contextObj.content = this.typesUtilsService.getLocaleString(
          help?.contextualHelpDetails?.dashboardHelp,
        );
        break;
      case helpContextEnum.AskUser:
        contextObj.title = this.translateService.instant(
          'labels.ASK_USER_HELP',
        );
        contextObj.content = this.typesUtilsService.getLocaleString(
          help?.contextualHelpDetails?.askUserHelp,
        );
        if (
          help?.contextualHelpDetails?.askUserFieldsHelp?.length &&
          flattedFields
        ) {
          help?.contextualHelpDetails.askUserFieldsHelp
            .filter((fieldHelp) => !!fieldHelp.contextualHelp)
            .map((fieldHelp) =>
              contextObj.children.push(
                this._getFieldContextualHelp(
                  flattedFields,
                  fieldHelp,
                  help.contextualHelpDetails?.askUserFieldsHelp,
                ),
              ),
            );
        }
        break;
    }
    // check if some entity fields are not added to entity fields help
    if (flattedFields && Object.keys(flattedFields).length > 0) {
      Object.keys(flattedFields).forEach((field) => {
        if (
          (flattedFields[field]?.contextualHelp ||
            (flattedFields[field]?.children &&
              Object.keys(flattedFields[field].children)?.filter(
                (x) => flattedFields[field].children[x].contextualHelp,
              ))) &&
          !contextObj.children.find(
            (eh) =>
              eh.title ===
              this.typesUtilsService.getLocaleString(flattedFields[field].name),
          )
        ) {
          contextObj.children.push({
            title: this.typesUtilsService.getLocaleString(
              flattedFields[field].name,
            ),
            content: this.typesUtilsService.getLocaleString(
              flattedFields[field].contextualHelp,
            ),
            children: flattedFields[field].children
              ? Object.keys(flattedFields[field].children)
                  ?.filter(
                    (x) => flattedFields[field].children[x].contextualHelp,
                  )
                  ?.map((childField) => ({
                    title: this.typesUtilsService.getLocaleString(
                      flattedFields[field].children[childField].name,
                    ),
                    content: this.typesUtilsService.getLocaleString(
                      flattedFields[field].children[childField].contextualHelp,
                    ),
                  })) || []
              : [],
          });
        }
      });
    }

    if (contextObj.content || contextObj.children.length) {
      helpContentArray.push(contextObj);
    }
    return helpContentArray;
  }

  private _getFieldContextualHelp(
    flattedFields,
    fieldHelp,
    fieldsContextualHelp?,
  ) {
    return {
      title: this.typesUtilsService.getLocaleString(
        flattedFields[fieldHelp.fieldName]?.name,
      ),
      content:
        this.typesUtilsService.getLocaleString(fieldHelp.contextualHelp) ??
        this.typesUtilsService.getLocaleString(
          flattedFields[fieldHelp.fieldName].contextualHelp,
        ),
      children: flattedFields[fieldHelp.fieldName].children
        ? Object.keys(flattedFields[fieldHelp.fieldName].children)
            ?.filter(
              (x) =>
                fieldsContextualHelp?.find((x) => x.fieldName === x)
                  ?.contextualHelp ||
                flattedFields[fieldHelp.fieldName].children[x].contextualHelp,
            )
            ?.map((childField) => ({
              title: this.typesUtilsService.getLocaleString(
                flattedFields[fieldHelp.fieldName].children[childField].name,
              ),
              content: this.typesUtilsService.getLocaleString(
                fieldsContextualHelp?.find((x) => x.fieldName === childField)
                  ?.contextualHelp ??
                  flattedFields[fieldHelp.fieldName].children[childField]
                    .contextualHelp,
              ),
            })) || []
        : [],
    };
  }
}
